import React from 'react';
import { createPortal } from 'react-dom';

// Dialog component removed - using direct full-screen modal
import { RotateCw, FlipHorizontal, ZoomIn, ZoomOut, ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';

import { PersonalisationLoader } from '../PersonalisationLoader';
import { useViewModel } from './useViewModel';
import type { PhotoEditorProps } from './types';
import { ImagePreview, FileInput, UploadButton, NoFileChosen } from './ui/renderHelpers';

import themeStyles from './photoEditorTheme.module.css';

/**
 * PhotoEditor Component
 *
 * A simple photo editing component that provides:
 * - Image upload functionality
 * - Full-screen modal editor
 * - Rotate, flip, and zoom capabilities
 * - Blob output for processed images
 */
export const PhotoEditor = (props: PhotoEditorProps) => {
  const {
    stepImageUrl,
    uploadType,
    className,
    accept,
    disabled,
    id,
    label,
    showStarMapSlot = false,
    maskZones
  } = props;
  const isEngraving = uploadType === 'engraving';
  // For star map, apply blend mode but not contrast
  const shouldApplyBlendMode = isEngraving || showStarMapSlot;
  const shouldApplyContrast = isEngraving && !showStarMapSlot;
  const isMultiZoneMode = maskZones && maskZones.length > 0;

  const {
    // State
    isEditorOpen,
    selectedImage,
    imageUrl,
    rotation,
    scale,
    flipX,
    error,
    isLoading,
    isProcessing,
    canvasPreview,
    isGeneratingPreview,
    isMaskReady,

    // Multi-zone state
    zones,
    zoneImages,
    activeZoneIndex,
    allZonesHaveFiles,
    fileInputRefs,
    activeZoneGlow,

    // Refs
    fileInputRef,
    stepImageRef,

    // Handlers
    handleFileSelect,
    handleSave,
    handleClose,
    handleClear,
    handleButtonClick,
    handleStepImageLoad,
    handleZoneButtonClick,

    // Editor handlers
    handleRotateLeft,
    handleRotateRight,
    handleFlipX,
    handleZoomOut,
    handleZoomIn,
    handleMoveLeft,
    handleMoveRight,
    handleMoveUp,
    handleMoveDown,
    handleResetAll,

    // Computed values
    buttonClasses,
    zoneButtonClasses
  } = useViewModel(props);

  // Create portal for full-screen modal using HTML dialog element
  const modalPortal = isEditorOpen
    ? createPortal(
        <dialog
          ref={(dialog) => {
            if (dialog && isEditorOpen && !dialog.open) {
              dialog.showModal();
            }
          }}
          className={themeStyles['photo-editor__modal']}
          style={{
            // Safari-specific fixes for proper container sizing
            minWidth: '100vw',
            minHeight: '100vh'
          }}
        >
          <PersonalisationLoader isVisible={isProcessing} />

          <div className={themeStyles['photo-editor__modal-header']}>
            <div className={themeStyles['photo-editor__modal-header-content']}>
              <h2 className="heading-2xl">Edit Photo</h2>
              <div className={themeStyles['photo-editor__modal-header-controls']}>
                <button
                  className={'body-sm button-reset ' + themeStyles['photo-editor__modal-reset']}
                  onClick={handleResetAll}
                >
                  Reset All
                </button>
                <button
                  className={themeStyles['photo-editor__header-close'] + ' svg-wrapper button-reset'}
                  onClick={handleClose}
                  disabled={isProcessing}
                  aria-label="Cancel & Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={themeStyles.icon}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z"
                      fill="#171717"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className={themeStyles['photo-editor__modal-content']}>
            {/* Image Preview Area */}
            <div className={themeStyles['photo-editor__image-area']} id="photo-editor-canvas-container">
              <div
                id="photo-editor-canvas"
                className={themeStyles['photo-editor__canvas-container']}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  margin: 'auto'
                }}
              >
                {/* Step Image Background - absolutely positioned but with zIndex: 1 */}
                <div style={{ position: 'relative' }}>
                  {stepImageUrl && (
                    <img
                      ref={stepImageRef}
                      src={stepImageUrl}
                      alt="Step background"
                      className={themeStyles['photo-editor__step-background']}
                      onError={() => {}}
                      onLoad={handleStepImageLoad}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  )}

                  {/* Canvas Preview - Uses same logic as save handlers */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      zIndex: 10,
                      mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                      // Safari-specific fixes for proper container sizing
                      width: '100%',
                      height: '100%'
                    }}
                  >
                    <div
                      id="photo-editor-image-container"
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        // Safari-specific fixes for container expansion
                        minWidth: '100%',
                        minHeight: '100%',
                        mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                        // Hide mask until it's in final position
                        opacity: isMaskReady ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    >
                      {(isMultiZoneMode ? zoneImages.size > 0 : imageUrl) ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          {canvasPreview ? (
                            <img
                              id="photo-editor-canvas-preview"
                              src={canvasPreview}
                              alt="Canvas Preview"
                              className={themeStyles['photo-editor__canvas-preview']}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                                filter: shouldApplyContrast ? 'grayscale(100%) contrast(300%)' : 'none'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                color: '#666',
                                fontSize: '14px',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '8px'
                              }}
                            >
                              {isGeneratingPreview ? 'Generating canvas preview...' : 'Loading canvas preview...'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            color: '#999',
                            fontSize: '14px'
                          }}
                        >
                          {isMultiZoneMode ? 'Upload images for all zones to see preview' : 'No image selected'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls Panel */}
            <div className={themeStyles['photo-editor__controls-panel']}>
              {/* Rotation Controls */}
              <div
                className={
                  themeStyles['photo-editor__split-row'] + ' ' + themeStyles['photo-editor__split-row--three-columns']
                }
              >
                <div
                  className={
                    themeStyles['photo-editor__split-row-column'] +
                    ' ' +
                    themeStyles['photo-editor__split-row-column--2']
                  }
                >
                  <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>
                    Rotate ({rotation}&deg;)
                  </div>
                  <div className={themeStyles['photo-editor__button-row']}>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small button--icon-flip"
                      onClick={handleRotateLeft}
                    >
                      <RotateCw />
                      Left
                    </button>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small"
                      onClick={handleRotateRight}
                    >
                      <RotateCw />
                      Right
                    </button>
                  </div>
                </div>
                {/* Flip Control */}
                <div className={themeStyles['photo-editor__split-row-column']}>
                  <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>Flip</div>
                  <div className={themeStyles['photo-editor__button-row']}>
                    <button
                      type="button"
                      className={`button button--secondary button--black button--small${flipX ? ' active' : ''}`}
                      onClick={handleFlipX}
                    >
                      <FlipHorizontal />
                      Flip
                    </button>
                  </div>
                </div>
              </div>
              <div className={themeStyles['photo-editor__split-row']}>
                {/* Zoom Control */}
                <div className={themeStyles['photo-editor__split-row-column']}>
                  <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>
                    Zoom ({Math.round(scale * 100)}%)
                  </div>
                  <div className={themeStyles['photo-editor__button-row']}>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small"
                      onClick={handleZoomOut}
                      disabled={scale <= 0.1}
                    >
                      <ZoomOut />
                    </button>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small"
                      onClick={handleZoomIn}
                      disabled={scale >= 3}
                    >
                      <ZoomIn />
                    </button>
                  </div>
                </div>
                {/* Move Controls */}
                <div className={themeStyles['photo-editor__split-row-column']}>
                  <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>Move</div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small"
                      onClick={handleMoveUp}
                    >
                      <ArrowUp />
                    </button>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        className="button button--secondary button--black button--small"
                        onClick={handleMoveLeft}
                      >
                        <ArrowLeft />
                      </button>
                      <button
                        type="button"
                        className="button button--secondary button--black button--small"
                        onClick={handleMoveRight}
                      >
                        <ArrowRight />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="button button--secondary button--black button--small"
                      onClick={handleMoveDown}
                    >
                      <ArrowDown />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={themeStyles['photo-editor__modal-footer']}>
            {/* Zone buttons - only show in multi-zone mode */}
            {isMultiZoneMode && zones.length > 0 && (
              <div className={themeStyles['photo-editor__zone-buttons']}>
                {zones.map((zone, index) => (
                  <button
                    key={index}
                    type="button"
                    className={zoneButtonClasses(index)}
                    onClick={() => handleZoneButtonClick(index)}
                    disabled={isProcessing}
                    aria-label={`Select ${zone.zoneLabel} zone`}
                  >
                    {zone.zoneLabel}
                  </button>
                ))}
              </div>
            )}
            <div className={themeStyles['photo-editor__modal-footer-actions']}>
              <button
                onClick={handleClose}
                className={themeStyles['photo-editor__modal-footer-cancel'] + ' button button--secondary button--black'}
                type="button"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className={themeStyles['photo-editor__modal-footer-save'] + ' button button--primary button--orange'}
                type="button"
                disabled={isProcessing}
                aria-label="Save"
              >
                Save
              </button>
            </div>
          </div>
        </dialog>,
        document.getElementById('photo-editor-portal') || document.body
      )
    : null;

  return (
    <div className={`${themeStyles['photo-editor']} ${className}`}>
      {isMultiZoneMode ? (
        /* Multi-zone mode: render file inputs for each zone */
        <>
          {zones.map((zone, index) => {
            const zoneImage = zoneImages.get(index);
            const showGreenTick = fileInputRefs[index]?.current?.files?.length > 0;
            const buttonLabel = `${label} - ${zone.zoneLabel}`;

            return (
              <div key={index} className={themeStyles['photo-editor__zone-input-wrapper']}>
                {zoneImage && (
                  <ImagePreview
                    imageUrl={zoneImage.blobUrl}
                    filename={zoneImage.file.name}
                    onRemove={() => handleClear(index)}
                  />
                )}

                <FileInput
                  inputRef={fileInputRefs[index]}
                  accept={accept}
                  disabled={disabled}
                  id={`${id}-zone-${index}`}
                  ariaLabel={`File input for ${zone.zoneLabel}`}
                  onChange={(e) => handleFileSelect(e, index)}
                />

                <UploadButton
                  label={buttonLabel}
                  buttonClasses={buttonClasses}
                  isLoading={isLoading}
                  disabled={disabled ?? false}
                  onClick={() => handleButtonClick(index)}
                  ariaLabel={`${label ?? ''} for ${zone.zoneLabel}`}
                  showGreenTick={showGreenTick}
                />

                <NoFileChosen hasFile={!!zoneImage} />
              </div>
            );
          })}
        </>
      ) : (
        /* Single zone mode: existing behavior */
        <>
          {imageUrl && selectedImage && (
            <ImagePreview imageUrl={imageUrl} filename={selectedImage.name} onRemove={() => handleClear()} />
          )}

          <FileInput
            inputRef={fileInputRef}
            accept={accept}
            disabled={disabled}
            id={id ?? ''}
            ariaLabel="File input"
            onChange={(e) => handleFileSelect(e, 0)}
          />

          <UploadButton
            label={label ?? ''}
            buttonClasses={buttonClasses}
            isLoading={isLoading}
            disabled={disabled ?? false}
            onClick={() => handleButtonClick()}
            ariaLabel={label ?? ''}
          />

          <NoFileChosen hasFile={!!selectedImage} />
        </>
      )}

      {/* Error message */}
      {error && (
        <div className={themeStyles['photo-editor__error'] + ' body-sm font-medium'} role="alert">
          {error}
        </div>
      )}

      {/* Full Screen Photo Editor Modal */}
      {modalPortal}
    </div>
  );
};
